import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, ArrowLeft, Award, Star } from 'lucide-react';
import { useProducts } from '../contexts/ProductContext';
import ReviewCard from '../components/ReviewCard';
const ProductDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const {
    products
  } = useProducts();
  const product = products.find(p => p.id === id);
  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-131">
        <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-132">
          Product Not Found
        </h2>
        <p className="text-gray-600 mb-8" data-id="element-133">
          The product you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/products" className="text-emerald-600 hover:text-emerald-700 flex items-center justify-center" data-id="element-134">
          <ArrowLeft className="mr-2 h-5 w-5" data-id="element-135" />
          Back to Products
        </Link>
      </div>;
  }
  // Calculate average rating
  const averageRating = product.reviews.reduce((sum, review) => sum + review.rating, 0) / product.reviews.length;
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-136">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-137">
        {/* Breadcrumb */}
        <div className="mb-6" data-id="element-138">
          <Link to="/products" className="text-emerald-600 hover:text-emerald-700 flex items-center text-sm" data-id="element-139">
            <ArrowLeft className="mr-1 h-4 w-4" data-id="element-140" />
            Back to Products
          </Link>
        </div>
        {/* Product Overview */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8" data-id="element-141">
          <div className="md:flex" data-id="element-142">
            {/* Product Image */}
            <div className="md:w-1/2 h-64 md:h-auto" data-id="element-143">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-144" />
            </div>
            {/* Product Info */}
            <div className="md:w-1/2 p-6" data-id="element-145">
              <div className="flex items-center mb-2" data-id="element-146">
                <span className="bg-brand-100 text-brand-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center" data-id="element-147">
                  <Award className="h-3 w-3 mr-1" data-id="element-148" />
                  Dietitian Approved
                </span>
                <span className="ml-2 text-xs text-gray-500" data-id="element-149">
                  Category: {product.category}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-150">
                {product.name}
              </h1>
              <div className="flex items-center mb-4" data-id="element-151">
                <div className="flex text-yellow-400" data-id="element-152">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`h-5 w-5 ${i < Math.round(averageRating) ? 'fill-current' : 'stroke-current fill-transparent'}`} data-id="element-153" />)}
                </div>
                <span className="text-sm text-gray-600 ml-2" data-id="element-154">
                  {averageRating.toFixed(1)} ({product.reviews.length} dietitian{' '}
                  {product.reviews.length === 1 ? 'review' : 'reviews'})
                </span>
              </div>
              <p className="text-gray-700 mb-6" data-id="element-155">{product.description}</p>
              {product.ingredients && <div className="mb-6" data-id="element-156">
                  <h3 className="font-medium text-gray-900 mb-2" data-id="element-157">
                    Ingredients:
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-158">{product.ingredients}</p>
                </div>}
              <div className="flex items-center justify-between mt-8" data-id="element-159">
                <span className="text-2xl font-bold text-gray-900" data-id="element-160">
                  {product.price}
                </span>
                <a href={product.purchaseLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700" data-id="element-161">
                  Purchase Product
                  <ExternalLink className="ml-2 -mr-1 h-4 w-4" data-id="element-162" />
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* Dietitian Reviews */}
        <div className="mb-8" data-id="element-163">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-164">
            Dietitian Reviews
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-165">
            {product.reviews.map(review => <ReviewCard key={review.id} review={review} data-id="element-166" />)}
          </div>
        </div>
        {/* Related Products */}
        <div data-id="element-167">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-168">
            You Might Also Like
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-169">
            {products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 3).map(relatedProduct => <div key={relatedProduct.id} className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-170">
                  <Link to={`/products/${relatedProduct.id}`} data-id="element-171">
                    <div className="h-40 overflow-hidden" data-id="element-172">
                      <img src={relatedProduct.image} alt={relatedProduct.name} className="w-full h-full object-cover" data-id="element-173" />
                    </div>
                    <div className="p-4" data-id="element-174">
                      <h3 className="font-medium text-gray-900" data-id="element-175">
                        {relatedProduct.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-176">
                        {relatedProduct.shortDescription}
                      </p>
                    </div>
                  </Link>
                </div>)}
          </div>
        </div>
      </div>
    </div>;
};
export default ProductDetail;